import { Alert } from '@/components/alert';
import { Flex } from '@/components/layouting';
import { Spinner } from '@/components/Loaders/Spinner';
import { FormattedMessage, useIntl } from '@/components/formatted_message/FormattedMessage';
import { Typography } from '@/components/typography';
import { ButtonAnt } from '@/components/buttons/button/ButtonAnt';
import { message, QRCode } from 'antd';
import { useState, useEffect, type FC } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { useHistory } from 'react-router-dom';
import { TwoFactorStep } from '../enums/two-factor-step';
import type { EnableTwoFactorData } from '@/services/api/auth';
import { Page as pageMessages, SetupContent as messages } from '../messages';
import { Form, Input } from '@/components/form';
import { Icon } from '@/components/icon';
import { getSecretFromQuery } from '@/utils/getSecretFromQuery';

const { Paragraph } = Typography;

interface SetupContentProps {
  password: string | undefined;
  onStepChange: (step: TwoFactorStep) => void;
  onBackupCodesGenerated: (codes: string[]) => void;
}

export const SetupContent: FC<SetupContentProps> = ({ password, onStepChange, onBackupCodesGenerated }) => {
  const history = useHistory();
  const intl = useIntl();

  const {
    enableTwoFactorAsync,
    loading: { isLoadingEnableTwoFactor },
    error: { enableTwoFactor: enableTwoFactorError },
  } = useAuthContext();

  const [enableResponse, setEnableResponse] = useState<EnableTwoFactorData | null>(null);

  useEffect(() => {
    const enable2FA = async () => {
      // User is not coming directly from login workflow and must have reloaded page, since we don't want to
      // store password anywhere, we prompt him to enter it again
      if (!password) {
        return;
      }

      try {
        const data = await enableTwoFactorAsync({ password });
        setEnableResponse(data);
      } catch (e: unknown) {
        console.error('Error enabling two-factor authentication:', e);
      }
    };

    enable2FA();
  }, [enableTwoFactorAsync, password]);

  const handleContinueToVerification = () => {
    if (enableResponse?.backupCodes?.length) {
      onBackupCodesGenerated(enableResponse.backupCodes);
    }
    onStepChange(TwoFactorStep.Verify);
  };

  const handleCancel = () => {
    history.push('/');
  };

  const handleCopy = async () => {
    if (!enableResponse?.totpURI) return;

    const secretToken = getSecretFromQuery(enableResponse.totpURI ?? '');

    try {
      await navigator.clipboard.writeText(secretToken);
      message.success(intl.formatMessage(messages.keyCopiedToClipboard));
    } catch (_) {
      message.error('Unable to copy to clipboard. Please copy the key manually.');
    }
  };

  return (
    <Flex gap={24} vertical justify="center" align="center">
      {Boolean(enableTwoFactorError) && (
        <Alert
          showIcon
          description={<FormattedMessage {...pageMessages.alertMessage} />}
          type="error"
          data-cy="verify-error-alert"
        />
      )}
      <Paragraph>
        <FormattedMessage {...messages.description} />
      </Paragraph>

      {isLoadingEnableTwoFactor ? <Spinner /> : <QRCode value={enableResponse?.totpURI ?? ''} size={256} />}

      <Form.Item className="w-100" label={intl.formatMessage(messages.addKeyManually)} layout="vertical">
        <Input
          value={enableResponse?.totpURI ?? ''}
          readOnly
          addonAfter={<Icon fixedWidth iconString="copy" style={{ cursor: 'pointer' }} onClick={handleCopy} />}
        />
      </Form.Item>

      <ButtonAnt
        disabled={Boolean(enableTwoFactorError)}
        block
        loading={isLoadingEnableTwoFactor}
        type="primary"
        onClick={handleContinueToVerification}
      >
        <FormattedMessage {...messages.scanCompleted} />
      </ButtonAnt>
      <ButtonAnt block onClick={handleCancel}>
        <FormattedMessage {...messages.cancel} />
      </ButtonAnt>
    </Flex>
  );
};
